import { ImageNotSupportedOutlined, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { guideEditorApi, PlaceCandidate, PlaceSearchResult } from '../../services/guide-editor-api';
import { EditorGuide, findChapter, findPick, GuideOp, titleText } from './model';
import { resolveSiteUrl } from './urls';

export type PlaceDialogTarget =
  | { mode: 'add'; chapterId: string; index?: number }
  | { mode: 'change'; pickId: string };

export interface PlaceSubmitResult {
  ok: boolean;
  /** The backend's refusal (closed, too far, no address ...), shown inline. */
  message?: string;
}

interface Props {
  target: PlaceDialogTarget | null;
  guide: EditorGuide;
  slug: string;
  /** Unsaved edits that go up with the place, so the editor can say so. */
  pendingCount: number;
  onClose: () => void;
  onSubmit: (op: GuideOp) => Promise<PlaceSubmitResult>;
}

/** Exactly one of locationId / placeId, as the Content API requires. */
function placeRef(c: PlaceCandidate): { locationId: string } | { placeId: string } | null {
  if (c.tripsby_id) return { locationId: c.tripsby_id };
  if (c.google_place_id) return { placeId: c.google_place_id };
  return null;
}

const candidateKey = (c: PlaceCandidate) => c.tripsby_id || c.google_place_id || c.name;

export default function AddPlaceDialog({ target, guide, slug, pendingCount, onClose, onSubmit }: Props) {
  const open = target !== null;
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [position, setPosition] = useState<number>(-1); // -1 = the end
  const [result, setResult] = useState<PlaceSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const searchSeq = useRef(0);

  // Reset whenever the dialog opens for a (new) target.
  useEffect(() => {
    if (!target) return;
    setQ('');
    setCity(typeof guide.city === 'string' ? guide.city : '');
    setChapterId(target.mode === 'add' ? target.chapterId : '');
    setPosition(target.mode === 'add' && target.index !== undefined ? target.index : -1);
    setResult(null);
    setSearchError(null);
    setRefusal(null);
    setSubmitting(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    const cityName = city.trim();
    if (query.length < 2 || !cityName) {
      setResult(null);
      setSearching(false);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    setSearchError(null);
    const timer = setTimeout(async () => {
      try {
        const found = await guideEditorApi.searchPlaces({ q: query, city: cityName, slug, limit: 8 });
        if (seq === searchSeq.current) setResult(found);
      } catch (err: any) {
        if (seq === searchSeq.current) {
          setResult(null);
          setSearchError(err?.message || 'Place search failed');
        }
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [q, city, slug, open]);

  if (!target) return null;

  const chapters = guide.chapters ?? [];
  const targetChapter = chapterId ? findChapter(guide, chapterId)?.chapter : undefined;
  const pickCount = (targetChapter?.picks ?? []).length;
  const changing = target.mode === 'change' ? findPick(guide, target.pickId) : null;

  const choose = async (c: PlaceCandidate) => {
    const ref = placeRef(c);
    if (!ref || submitting) return;
    setRefusal(null);
    setSubmitting(candidateKey(c));
    const op: GuideOp =
      target.mode === 'add'
        ? {
            op: 'add_pick_from_place',
            chapterId,
            index: position < 0 || position > pickCount ? pickCount : position,
            ...ref,
          }
        : { op: 'set_venue_link', pickId: target.pickId, ...ref };
    const outcome = await onSubmit(op);
    setSubmitting(null);
    if (!outcome.ok) setRefusal(outcome.message || 'The place could not be added.');
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {target.mode === 'add'
          ? 'Add a place'
          : `Change place for "${changing ? titleText(changing.pick.title) : target.pickId}"`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, mt: 0.5 }}>
          <TextField
            label="Search for a place"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            autoFocus
            sx={{ flex: '2 1 220px' }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            inputProps={{ 'data-testid': 'place-search-input' }}
          />
          <TextField
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            size="small"
            sx={{ flex: '1 1 120px' }}
            required
          />
        </Box>
        {target.mode === 'add' && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ flex: '1 1 180px' }}>
              <InputLabel id="place-chapter-label">Chapter</InputLabel>
              <Select
                labelId="place-chapter-label"
                label="Chapter"
                value={chapterId}
                onChange={(e) => {
                  setChapterId(e.target.value);
                  setPosition(-1);
                }}
              >
                {chapters.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {titleText(c.title) || c.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 140px' }}>
              <InputLabel id="place-position-label">Position</InputLabel>
              <Select
                labelId="place-position-label"
                label="Position"
                value={position < 0 || position > pickCount ? -1 : position}
                onChange={(e) => setPosition(Number(e.target.value))}
              >
                <MenuItem value={-1}>At the end</MenuItem>
                {Array.from({ length: pickCount }).map((_, i) => (
                  <MenuItem key={i} value={i}>
                    Before place {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {target.mode === 'change' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The card keeps its own name; its address, map pin and link move to the place you pick.
          </Typography>
        )}
        {pendingCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your {pendingCount} unsaved {pendingCount === 1 ? 'change is' : 'changes are'} saved
            together with this place.
          </Alert>
        )}
        {refusal && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="place-refusal">
            {refusal}
          </Alert>
        )}
        {searchError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {searchError}
          </Alert>
        )}
        {searching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!searching && result && result.candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No places found. Try the name as it appears on the door.
          </Typography>
        )}
        {result && result.candidates.length > 0 && (
          <List dense data-testid="place-results">
            {result.candidates.map((c) => {
              const ref = placeRef(c);
              const key = candidateKey(c);
              return (
                <ListItemButton
                  key={key}
                  disabled={!ref || !!submitting}
                  onClick={() => choose(c)}
                  data-testid="place-result"
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={resolveSiteUrl(c.photoSrc)}>
                      <ImageNotSupportedOutlined fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c.name}
                    secondary={
                      <>
                        {c.displayAddress}
                        {c.distanceKm !== null && c.distanceKm !== undefined && (
                          <> · {c.distanceKm} km away</>
                        )}
                      </>
                    }
                  />
                  {submitting === key && <CircularProgress size={18} />}
                  {!c.tripsby_id && ref && (
                    <Chip size="small" variant="outlined" label="New to our places" sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
        {result && (result.sources.google === 'unavailable' || result.sources.ers === 'unavailable') && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Some place sources did not answer, so this list may be incomplete.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!submitting}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
